import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import dayjs from 'dayjs';
import PaymentsTableToolbar from '../../../../components/admin/PaymentsTableToolbar';
import {
  StatusChipFinanceiro,
  OrigemConfirmacaoBadge,
  FiltroAutocomplete,
} from '../../../../components/financeiro';
import type { FiltroAutocompleteOption } from '../../../../components/financeiro';
import { useTransacoesPlataforma } from '../../../../hooks/useTransacoesPlataforma';
import { formatCurrency } from '../../../../utils/money';
import type { PeriodoFinanceiro } from '../../../../types/financeiro';
import type { PaymentsFilter } from '../../../../types/pagamento';

// Aba "Transações" do hub financeiro da plataforma -- AC-13 (plano AC-A2). Recebe { periodo }
// por prop (dono é o hub — FinanceiroAdminPage.tsx) e é DONA do próprio filtro de liga desde
// 2026-08-18: antes ele era um combobox no topo do hub, que se apresentava como global mas só
// tinha efeito aqui e no ponto de entrada da aba Por liga — nas abas Resumo, Reembolsos e
// Conciliação ficava visível e preenchido sem filtrar nada. Chama
// useTransacoesPlataforma(periodo, ligaId) e reusa PaymentsTableToolbar (não recria o
// autocomplete debounced) com availableFilters=['torneio','atleta','status'] -- sem 'categoria'/
// 'data', o período vem do topo do hub. Ordem erro → loading → vazio → tabela
// (pages/admin/financeiro/tabs/PorLigaTab.tsx:181-217). Nenhum dado é buscado diretamente por
// service aqui — tudo vem do hook (inclusive `fetchTorneios`, cascateado por `ligaId`).
//
// Tipo de linha DELIBERADAMENTE local (estrutural, sem import de services/) — o hook devolve o
// tipo real (services/pagamentoInscricaoService.PagamentoInscricao), mas este arquivo não pode
// importar de services/ (VALIDATE da Task 6) — o compilador ainda checa a forma via inferência.

export interface TransacoesTabProps {
  periodo: PeriodoFinanceiro;
}

interface LinhaTransacao {
  id: string;
  codigoTransacao?: string | null;
  criadoEm?: string | null;
  dataPagamento?: string | null;
  meioPagamento?: string | null;
  status: string;
  origemConfirmacao?: string | null;
  valor?: number | null;
  inscricoes: {
    equipe?: { atletas?: { usuario?: { nome?: string } | null }[] } | null;
    categoriaTorneio?: {
      nome?: string;
      // `liga` é opcional no schema (Torneio.liga é nullable: torneio avulso, ou liga removida
      // via ON DELETE SET NULL) -- a coluna precisa saber viver sem ela.
      torneio?: { nome?: string; liga?: { id?: string; nome?: string } | null } | null;
    } | null;
  }[];
}

const nomesUnicos = (valores: (string | null | undefined)[]): string => {
  const filtrados = valores.filter((v): v is string => Boolean(v));
  return filtrados.length ? Array.from(new Set(filtrados)).join(', ') : '-';
};

const atletaNomes = (pagamento: LinhaTransacao): string =>
  nomesUnicos(
    (pagamento.inscricoes ?? []).flatMap(
      (inscricao) => (inscricao.equipe?.atletas ?? []).map((atleta) => atleta.usuario?.nome),
    ),
  );

// Torneio sem liga é estado legítimo, não anomalia — mesmo motivo já registrado na aba "Por
// liga": a coluna mostra "Sem liga" em vez de célula vazia, para não confundir "não pertence a
// nenhuma liga" com "o dado não veio".
const ligaNomes = (pagamento: LinhaTransacao): string => {
  const nomes = (pagamento.inscricoes ?? []).map(
    (inscricao) => inscricao.categoriaTorneio?.torneio?.liga?.nome,
  );
  return nomes.some(Boolean) ? nomesUnicos(nomes) : 'Sem liga';
};

const torneioNomes = (pagamento: LinhaTransacao): string =>
  nomesUnicos((pagamento.inscricoes ?? []).map((inscricao) => inscricao.categoriaTorneio?.torneio?.nome));

const categoriaNomes = (pagamento: LinhaTransacao): string =>
  nomesUnicos((pagamento.inscricoes ?? []).map((inscricao) => inscricao.categoriaTorneio?.nome));

const dataExibida = (pagamento: LinhaTransacao): string => {
  const valor = pagamento.dataPagamento || pagamento.criadoEm;
  return valor ? dayjs(valor).format('DD/MM/YYYY') : '-';
};

const TransacoesTab: React.FC<TransacoesTabProps> = ({ periodo }) => {
  const [liga, setLiga] = useState<FiltroAutocompleteOption | null>(null);
  const {
    items,
    totalItems,
    page,
    setPage,
    filtros,
    setFiltros,
    loading,
    error,
    refetch,
    fetchTorneios,
    fetchLigas,
  } = useTransacoesPlataforma(periodo, liga?.id ?? null);

  const handleApply = (filters: PaymentsFilter) => {
    setPage(0);
    setFiltros({
      torneioId: filters.torneioId ?? null,
      atletaId: filters.atletaId ?? null,
      status: filters.status ?? null,
    });
  };

  const totalPaginas = Math.max(1, Math.ceil(totalItems / 10));

  return (
    <Box>
      <Box mb={2} maxWidth={320} data-testid="transacoes-filtro-liga">
        <FiltroAutocomplete label="Liga" value={liga} onChange={setLiga} fetchOptions={fetchLigas} />
      </Box>

      <PaymentsTableToolbar
        availableFilters={['torneio', 'atleta', 'status']}
        fetchTorneios={fetchTorneios}
        onApply={handleApply}
      />

      {error ? (
        <Box textAlign="center" py={5} data-testid="transacoes-erro">
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Tentar novamente
              </Button>
            }
          >
            Erro ao carregar transações. Tente novamente.
          </Alert>
        </Box>
      ) : loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Box textAlign="center" py={5} data-testid="transacoes-vazio">
          Nenhuma transação no período selecionado.
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Atleta</TableCell>
                  <TableCell>Liga</TableCell>
                  <TableCell>Torneio</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell>Meio</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.codigoTransacao}</TableCell>
                    <TableCell>{atletaNomes(item)}</TableCell>
                    <TableCell>{ligaNomes(item)}</TableCell>
                    <TableCell>{torneioNomes(item)}</TableCell>
                    <TableCell>{categoriaNomes(item)}</TableCell>
                    <TableCell>{formatCurrency(item.valor ?? 0)}</TableCell>
                    <TableCell>
                      <StatusChipFinanceiro status={item.status} />
                    </TableCell>
                    <TableCell>
                      <OrigemConfirmacaoBadge status={item.origemConfirmacao} />
                    </TableCell>
                    <TableCell>{item.meioPagamento ?? '-'}</TableCell>
                    <TableCell>{dataExibida(item)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} mt={2}>
            <Button size="small" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Anterior
            </Button>
            <Box component="span">
              Página {page + 1} de {totalPaginas}
            </Box>
            <Button size="small" disabled={page + 1 >= totalPaginas} onClick={() => setPage(page + 1)}>
              Próxima
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TransacoesTab;
